import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import {
  FaShoppingCart,
  FaSearch,
  FaEye,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaChevronUp,
  FaFilter,
  FaChevronDown,
} from 'react-icons/fa';
import './readyproducts.scss';
import withAuth from '../../../auth/WithAuth';
import FurnitureNavbar from '../../../../components/layout/Nav';
import { serverUrlAuth } from '../../../../serverUrl';
import { toast } from 'react-toastify';

const MOCK_CATEGORIES = [
  { id: 1, name: 'Paintings', icon: '🎨', count: 156 },
  { id: 2, name: 'furniture', icon: '🏺', count: 89 },
  { id: 3, name: 'Sculptures', icon: '🗿', count: 67 },
  { id: 4, name: 'Jewelry', icon: '💍', count: 234 },
  { id: 5, name: 'Woven', icon: '🧺', count: 45 },
  { id: 6, name: 'Metalwork', icon: '🔨', count: 78 },
  { id: 7, name: 'Textiles', icon: '🧶', count: 123 },
];

// Budget range presets
const BUDGET_PRESETS = [
  { label: 'Under KSh 5,000', min: 0, max: 5000 },
  { label: 'KSh 5,000 - KSh 10,000', min: 5000, max: 10000 },
  { label: 'KSh 10,000 - KSh 25,000', min: 10000, max: 25000 },
  { label: 'KSh 25,000 - KSh 50,000', min: 25000, max: 50000 },
  { label: 'KSh 50,000 - KSh 100,000', min: 50000, max: 100000 },
  { label: 'Over KSh 100,000', min: 100000, max: Infinity },
];

const ReadyProductsView = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [loadingItems, setLoadingItems] = useState({});
  const [wishlist, setWishlist] = useState([]);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: '',
  });
  const [sortBy, setSortBy] = useState('featured');
  const [priceRange, setPriceRange] = useState({ min: 0, max: 1000000 });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedBudgetPreset, setSelectedBudgetPreset] = useState(null);

  // Quick view modal state
  const [showQuickView, setShowQuickView] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    setCategories(MOCK_CATEGORIES);
    fetchProducts();
    fetchCart();
    loadWishlist();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await serverUrlAuth.get(
        'projects/client-list-ready-made-products/',
      );
      console.log('Products:', response.data);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCart = async () => {
    try {
      const response = await serverUrlAuth.get('cart/');
      console.log('Cart response:', response.data);

      const cartData = response.data;
      const cartItemsArray = cartData.items || [];

      const transformedCart = cartItemsArray.map((item) => ({
        id: item.id,
        productId: item.product?.id,
        name: item.product?.name,
        price: parseFloat(item.product?.price || 0),
        quantity: item.quantity,
        stock: item.product?.stock || 0,
        images: item.product?.images || [],
      }));

      console.log('Transformed cart:', transformedCart);
      setCart(transformedCart);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  const handleScroll = () => {
    if (window.pageYOffset > 300) {
      setShowScrollTop(true);
    } else {
      setShowScrollTop(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadWishlist = () => {
    const savedWishlist = localStorage.getItem('wishlist');
    if (savedWishlist) {
      setWishlist(JSON.parse(savedWishlist));
    }
  };

  const addToCart = async (product, quantity = 1) => {
    if (product.stock === 0) {
      toast.error('Sorry, this item is out of stock!');
      return;
    }

    setLoadingItems((prev) => ({ ...prev, [product.id]: true }));

    try {
      const response = await serverUrlAuth.post('cart/add/', {
        product_id: product.id,
        quantity: quantity,
      });

      console.log('Add to cart response:', response.data);
      await fetchCart();
      toast.success(`${quantity} ${product.name} added to cart!`);

      if (showQuickView) {
        handleCloseQuickView();
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error(error.response?.data?.error || 'Failed to add to cart');
    } finally {
      setLoadingItems((prev) => ({ ...prev, [product.id]: false }));
    }
  };

  const getProductImage = (product) => {
    if (product.images && product.images.length > 0) {
      return product.images[0].image || product.images[0];
    }
    return 'https://via.placeholder.com/300x200?text=No+Image';
  };

  const getAllProductImages = (product) => {
    if (product.images && product.images.length > 0) {
      return product.images.map((img) => img.image || img);
    }
    return ['https://via.placeholder.com/300x200?text=No+Image'];
  };

  const handleQuickView = (product) => {
    setSelectedProduct({
      ...product,
      quantity: 1,
      currentImageIndex: 0,
    });
    setShowQuickView(true);
  };

  const handleCloseQuickView = () => {
    setShowQuickView(false);
    setSelectedProduct(null);
  };

  // Handle budget preset selection
  const handleBudgetPresetSelect = (preset) => {
    setSelectedBudgetPreset(preset.label);
    setPriceRange({ min: preset.min, max: preset.max });
    setCurrentPage(1); // Reset to first page
  };

  // Handle custom price range input
  const handleCustomPriceRange = (type, value) => {
    let newMin = priceRange.min;
    let newMax = priceRange.max;

    if (type === 'min') {
      newMin = value;
    } else {
      newMax = value;
    }

    setPriceRange({ min: newMin, max: newMax });
    setSelectedBudgetPreset(null); // Clear preset selection when custom range is used
    setCurrentPage(1); // Reset to first page
  };

  // Clear all filters including budget
  const clearAllFilters = () => {
    setSelectedCategory('all');
    setSearchTerm('');
    setPriceRange({ min: 0, max: 100000 });
    setSortBy('featured');
    setSelectedBudgetPreset(null);
    setShowMobileFilters(false);
  };

  // Filter and sort products
  const filteredProducts = products
    .filter((product) => {
      const matchesSearch =
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        selectedCategory === 'all' ||
        product.category?.toLowerCase() ===
          categories
            .find((c) => c.id === parseInt(selectedCategory))
            ?.name.toLowerCase();

      const matchesPrice =
        parseFloat(product.price) >= priceRange.min &&
        parseFloat(product.price) <= priceRange.max;

      return matchesSearch && matchesCategory && matchesPrice;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return parseFloat(a.price) - parseFloat(b.price);
        case 'price-high':
          return parseFloat(b.price) - parseFloat(a.price);
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        default:
          return 0;
      }
    });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProducts.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    if (window.innerWidth <= 768) {
      document
        .querySelector('.products-grid')
        ?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <FurnitureNavbar />
      <div className="ready-products-page">
        {/* Notification Toast */}
        {notification.show && (
          <div className={`notification-toast ${notification.type}`}>
            {notification.message}
            <button
              className="close-btn"
              onClick={() => setNotification({ ...notification, show: false })}
            >
              <FaTimes />
            </button>
          </div>
        )}

        {/* Hero Banner */}
        <div className="shop-hero">
          <div className="hero-content">
            <h1>Ready Made Products</h1>
            <p>
              Discover unique, handcrafted pieces from talented artisans around
              the world.
            </p>
          </div>
        </div>

        <div className="shop-container">
          {/* Mobile Filter Toggle */}
          <button
            className="mobile-filter-toggle"
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            aria-label="Toggle filters"
          >
            <FaFilter /> Filters
            <span className="filter-count">
              {selectedCategory !== 'all' ||
              searchTerm ||
              priceRange.min > 0 ||
              priceRange.max < 100000
                ? '●'
                : ''}
            </span>
            <FaChevronDown
              className={`arrow ${showMobileFilters ? 'open' : ''}`}
            />
          </button>

          <div className="shop-layout">
            {/* Sidebar Filters with Budget Section */}
            <aside
              className={`shop-sidebar ${showMobileFilters ? 'show' : ''}`}
            >
              <div className="sidebar-header">
                <h3>Filters</h3>
                <button
                  className="close-sidebar"
                  onClick={() => setShowMobileFilters(false)}
                  aria-label="Close filters"
                >
                  <FaTimes />
                </button>
              </div>

              {/* Categories Filter */}
              {/* <div className="filter-section">
                <h4>Categories</h4>
                <div className="category-list">
                  <button
                    className={`category-item ${selectedCategory === 'all' ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedCategory('all');
                      setShowMobileFilters(false);
                    }}
                  >
                    <span>All Categories</span>
                    <span className="count">{products.length}</span>
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      className={`category-item ${selectedCategory === cat.id.toString() ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedCategory(cat.id.toString());
                        setShowMobileFilters(false);
                      }}
                    >
                      <span>
                        {cat.icon} {cat.name}
                      </span>
                      <span className="count">{cat.count}</span>
                    </button>
                  ))}
                </div>
              </div> */}

              {/* Budget Filter Section - NEW */}
              <div className="filter-section">
                <h4>Filter by Budget</h4>

                {/* Budget Presets */}
                <div className="budget-presets">
                  {BUDGET_PRESETS.map((preset) => (
                    <button
                      key={preset.label}
                      className={`budget-preset-btn ${selectedBudgetPreset === preset.label ? 'active' : ''}`}
                      onClick={() => handleBudgetPresetSelect(preset)}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>

                {/* Custom Price Range Input */}
                {/* <div className="custom-price-range">
                  <p className="range-label">Or set custom range:</p>
                  <div className="price-inputs">
                    <div className="input-group">
                      <label htmlFor="min-price">Min (KSh)</label>
                      <input
                        id="min-price"
                        type="number"
                        value={priceRange.min}
                        onChange={(e) =>
                          handleCustomPriceRange('min', Number(e.target.value))
                        }
                        placeholder="0"
                        min="0"
                      />
                    </div>
                    <span className="range-separator">to</span>
                    <div className="input-group">
                      <label htmlFor="max-price">Max (KSh)</label>
                      <input
                        id="max-price"
                        type="number"
                        value={priceRange.max === Infinity ? '' : priceRange.max}
                        onChange={(e) =>
                          handleCustomPriceRange('max', Number(e.target.value))
                        }
                        placeholder="Any"
                        min="0"
                      />
                    </div>
                  </div>
                </div> */}

                {/* Active Budget Range Display */}
                {(priceRange.min > 0 || priceRange.max < 100000) && (
                  <div className="active-budget-range">
                    <span>Current budget: </span>
                    <strong>
                      {priceRange.min > 0
                        ? `KSh ${priceRange.min.toLocaleString()}`
                        : 'KSh 0'}{' '}
                      -{' '}
                      {priceRange.max === Infinity
                        ? 'Any'
                        : `KSh ${priceRange.max.toLocaleString()}`}
                    </strong>
                    <button
                      className="clear-budget"
                      onClick={() => {
                        setPriceRange({ min: 0, max: 100000 });
                        setSelectedBudgetPreset(null);
                      }}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>

              {/* Price Range Filter (Legacy - keeping for backward compatibility) */}
              <div className="filter-section">
                <h4>Price Range (KSh)</h4>
                <div className="price-range">
                  <div className="price-inputs">
                    <div className="input-group">
                      <label htmlFor="min-price-alt">Min</label>
                      <input
                        id="min-price-alt"
                        type="number"
                        value={priceRange.min}
                        onChange={(e) =>
                          handleCustomPriceRange('min', Number(e.target.value))
                        }
                        placeholder="0"
                        min="0"
                      />
                    </div>
                    <span className="range-separator">to</span>
                    <div className="input-group">
                      <label htmlFor="max-price-alt">Max</label>
                      <input
                        id="max-price-alt"
                        type="number"
                        value={
                          priceRange.max === Infinity ? '' : priceRange.max
                        }
                        onChange={(e) =>
                          handleCustomPriceRange('max', Number(e.target.value))
                        }
                        placeholder="100000"
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Filter Actions */}
              <div className="filter-actions">
                <button
                  className="apply-filters-btn"
                  onClick={() => setShowMobileFilters(false)}
                >
                  Apply Filters
                </button>
                <button className="clear-filters-btn" onClick={clearAllFilters}>
                  Clear All
                </button>
              </div>
            </aside>

            {/* Main Content */}
            <main className="shop-main">
              {/* Search and Sort Bar */}
              <div className="shop-toolbar">
                <div className="search-box">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    aria-label="Search products"
                  />
                  {searchTerm && (
                    <button
                      className="clear-search"
                      onClick={() => setSearchTerm('')}
                      aria-label="Clear search"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>

                <div className="sort-box">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    aria-label="Sort products by"
                  >
                    <option value="featured">Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="newest">Newest</option>
                  </select>
                </div>
              </div>

              {/* Active Filters Display */}
              {(selectedCategory !== 'all' ||
                searchTerm ||
                priceRange.min > 0 ||
                priceRange.max < 100000) && (
                <div className="active-filters">
                  <span>Active filters:</span>
                  {selectedCategory !== 'all' && (
                    <span className="filter-tag">
                      {
                        categories.find(
                          (c) => c.id === parseInt(selectedCategory),
                        )?.name
                      }
                      <button onClick={() => setSelectedCategory('all')}>
                        ×
                      </button>
                    </span>
                  )}
                  {searchTerm && (
                    <span className="filter-tag">
                      Search: {searchTerm}
                      <button onClick={() => setSearchTerm('')}>×</button>
                    </span>
                  )}
                  {(priceRange.min > 0 || priceRange.max < 100000) && (
                    <span className="filter-tag">
                      Budget:{' '}
                      {priceRange.min > 0
                        ? `KSh ${priceRange.min.toLocaleString()}`
                        : 'KSh 0'}{' '}
                      -{' '}
                      {priceRange.max === Infinity
                        ? 'Any'
                        : `KSh ${priceRange.max.toLocaleString()}`}
                      <button
                        onClick={() => {
                          setPriceRange({ min: 0, max: 100000 });
                          setSelectedBudgetPreset(null);
                        }}
                      >
                        ×
                      </button>
                    </span>
                  )}
                  <button
                    className="clear-all-filters"
                    onClick={clearAllFilters}
                  >
                    Clear all
                  </button>
                </div>
              )}

              {/* Results Info */}
              {!loading && filteredProducts.length > 0 && (
                <div className="results-info">
                  <p>
                    Showing{' '}
                    <span>
                      {indexOfFirstItem + 1}-
                      {Math.min(indexOfLastItem, filteredProducts.length)}
                    </span>{' '}
                    of <span>{filteredProducts.length}</span> products
                  </p>
                </div>
              )}

              {/* Products Grid */}
              {loading ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Discovering amazing creations...</p>
                </div>
              ) : (
                <>
                  <div className="products-grid">
                    {currentItems.length > 0 ? (
                      currentItems.map((product) => (
                        <div
                          key={product.id}
                          className={`product-card ${product.stock === 0 ? 'out-of-stock' : ''}`}
                        >
                          <div className="product-image">
                            <img
                              src={getProductImage(product)}
                              alt={product.name}
                              loading="lazy"
                            />
                            <div className="product-overlay">
                              <button
                                className="quick-view-btn"
                                onClick={() => handleQuickView(product)}
                                aria-label={`Quick view ${product.name}`}
                              >
                                <FaEye /> Quick View
                              </button>
                            </div>
                          </div>

                          <div className="product-info">
                            <h3 className="product-title">{product.name}</h3>

                            <div className="product-price">
                              <span className="current-price">
                                KSh {parseFloat(product.price).toLocaleString()}
                              </span>
                            </div>

                            <div className="product-meta">
                              <span
                                className="stock-status"
                                style={{
                                  color:
                                    product.stock > 0 ? '#28a745' : '#dc3545',
                                }}
                              >
                                {product.stock > 0
                                  ? `${product.stock} in stock`
                                  : 'Out of stock'}
                              </span>
                            </div>

                            <div className="product-actions">
                              <button
                                className="add-to-cart-btn"
                                onClick={() => addToCart(product, 1)}
                                disabled={
                                  product.stock === 0 ||
                                  loadingItems[product.id]
                                }
                              >
                                {loadingItems[product.id] ? (
                                  <span className="btn-text">Adding...</span>
                                ) : (
                                  <>
                                    <FaShoppingCart />
                                    <span className="btn-text">
                                      {product.stock === 0
                                        ? 'Out of Stock'
                                        : 'Add to Cart'}
                                    </span>
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-results">
                        <h3>No products found</h3>
                        <p>Try adjusting your search or filter criteria</p>
                        <button className="clear-btn" onClick={clearAllFilters}>
                          Clear All Filters
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Pagination */}
                  {filteredProducts.length > itemsPerPage && (
                    <div className="pagination">
                      <button
                        className="pagination-btn prev"
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        aria-label="Previous page"
                      >
                        <FaChevronLeft />
                      </button>

                      <div className="page-numbers">
                        {Array.from(
                          { length: Math.min(5, totalPages) },
                          (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }

                            return (
                              <button
                                key={pageNum}
                                className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                                onClick={() => paginate(pageNum)}
                                aria-label={`Page ${pageNum}`}
                                aria-current={
                                  currentPage === pageNum ? 'page' : undefined
                                }
                              >
                                {pageNum}
                              </button>
                            );
                          },
                        )}
                      </div>

                      <button
                        className="pagination-btn next"
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        aria-label="Next page"
                      >
                        <FaChevronRight />
                      </button>
                    </div>
                  )}
                </>
              )}
            </main>
          </div>
        </div>

        {/* Quick View Modal */}
        <Modal
          show={showQuickView}
          onHide={handleCloseQuickView}
          size="lg"
          centered
          className="quick-view-modal"
        >
          {selectedProduct && (
            <>
              <Modal.Header closeButton>
                <h2>{selectedProduct.name}</h2>
              </Modal.Header>
              <Modal.Body>
                <div className="quick-view-content">
                  <div className="quick-view-images">
                    <div className="main-image-carousel">
                      {selectedProduct.images &&
                      selectedProduct.images.length > 0 ? (
                        <>
                          <img
                            src={
                              selectedProduct.currentImageIndex !== undefined
                                ? selectedProduct.images[
                                    selectedProduct.currentImageIndex
                                  ].image ||
                                  selectedProduct.images[
                                    selectedProduct.currentImageIndex
                                  ]
                                : getProductImage(selectedProduct)
                            }
                            alt={selectedProduct.name}
                            className="main-image"
                          />
                          {selectedProduct.images.length > 1 && (
                            <>
                              <button
                                className="carousel-nav prev"
                                onClick={() => {
                                  const currentIndex =
                                    selectedProduct.currentImageIndex || 0;
                                  const newIndex =
                                    currentIndex === 0
                                      ? selectedProduct.images.length - 1
                                      : currentIndex - 1;
                                  setSelectedProduct({
                                    ...selectedProduct,
                                    currentImageIndex: newIndex,
                                  });
                                }}
                              >
                                <FaChevronLeft />
                              </button>
                              <button
                                className="carousel-nav next"
                                onClick={() => {
                                  const currentIndex =
                                    selectedProduct.currentImageIndex || 0;
                                  const newIndex =
                                    currentIndex ===
                                    selectedProduct.images.length - 1
                                      ? 0
                                      : currentIndex + 1;
                                  setSelectedProduct({
                                    ...selectedProduct,
                                    currentImageIndex: newIndex,
                                  });
                                }}
                              >
                                <FaChevronRight />
                              </button>
                              <div className="image-counter">
                                {(selectedProduct.currentImageIndex || 0) + 1} /{' '}
                                {selectedProduct.images.length}
                              </div>
                            </>
                          )}
                        </>
                      ) : (
                        <img
                          src="https://via.placeholder.com/500x400?text=No+Image"
                          alt="No available"
                          className="main-image"
                        />
                      )}
                    </div>

                    {/* Thumbnail Navigation */}
                    {selectedProduct.images &&
                      selectedProduct.images.length > 1 && (
                        <div className="image-thumbnails">
                          {getAllProductImages(selectedProduct).map(
                            (img, index) => (
                              <img
                                key={index}
                                src={img}
                                alt={`${selectedProduct.name} ${index + 1}`}
                                className={`thumbnail ${(selectedProduct.currentImageIndex || 0) === index ? 'active' : ''}`}
                                onClick={() =>
                                  setSelectedProduct({
                                    ...selectedProduct,
                                    currentImageIndex: index,
                                  })
                                }
                              />
                            ),
                          )}
                        </div>
                      )}
                  </div>

                  <div className="quick-view-details">
                    <p className="description">{selectedProduct.description}</p>
                    <div className="price-section">
                      <span className="price">
                        KSh {parseFloat(selectedProduct.price).toLocaleString()}
                      </span>
                      <span
                        className="stock-badge"
                        style={{
                          color:
                            selectedProduct.stock > 0 ? '#28a745' : '#dc3545',
                        }}
                      >
                        {selectedProduct.stock > 0
                          ? `${selectedProduct.stock} in stock`
                          : 'Out of stock'}
                      </span>
                    </div>
                    <div className="category">
                      <strong>Category:</strong> {selectedProduct.category}
                    </div>

                    {/* Quantity Selector */}
                    {selectedProduct.stock > 0 && (
                      <div className="quantity-selector">
                        <label>Quantity:</label>
                        <div className="quantity-controls">
                          <button
                            type="button"
                            onClick={() => {
                              const newQuantity = Math.max(
                                1,
                                (selectedProduct.quantity || 1) - 1,
                              );
                              setSelectedProduct({
                                ...selectedProduct,
                                quantity: newQuantity,
                              });
                            }}
                            disabled={selectedProduct.quantity <= 1}
                            className="quantity-btn"
                          >
                            -
                          </button>
                          <input
                            type="number"
                            min="1"
                            max={selectedProduct.stock}
                            value={selectedProduct.quantity || 1}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 1;
                              const newQuantity = Math.min(
                                selectedProduct.stock,
                                Math.max(1, value),
                              );
                              setSelectedProduct({
                                ...selectedProduct,
                                quantity: newQuantity,
                              });
                            }}
                            className="quantity-input"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newQuantity = Math.min(
                                selectedProduct.stock,
                                (selectedProduct.quantity || 1) + 1,
                              );
                              setSelectedProduct({
                                ...selectedProduct,
                                quantity: newQuantity,
                              });
                            }}
                            disabled={
                              selectedProduct.quantity >= selectedProduct.stock
                            }
                            className="quantity-btn"
                          >
                            +
                          </button>
                        </div>
                        <span className="stock-limit">
                          Max: {selectedProduct.stock}
                        </span>
                      </div>
                    )}

                    <div className="quick-view-actions">
                      <button
                        className="add-to-cart-btn large"
                        onClick={() =>
                          addToCart(
                            selectedProduct,
                            selectedProduct.quantity || 1,
                          )
                        }
                        disabled={
                          selectedProduct.stock === 0 ||
                          loadingItems[selectedProduct.id]
                        }
                      >
                        {loadingItems[selectedProduct.id] ? (
                          'Adding...'
                        ) : (
                          <>
                            <FaShoppingCart />
                            {selectedProduct.stock === 0
                              ? 'Out of Stock'
                              : 'Add to Cart'}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </Modal.Body>
            </>
          )}
        </Modal>

        {/* Floating Cart Preview */}
        {cart.length > 0 && (
          <div className="floating-cart">
            <Link
              to="/cart"
              className="floating-cart-content"
              aria-label="View cart"
            >
              <FaShoppingCart className="cart-icon" />
              <div className="cart-info">
                <span className="cart-count">{/* Cart count */}</span>
              </div>
            </Link>
          </div>
        )}

        {/* Scroll to Top Button */}
        {showScrollTop && (
          <button
            className="scroll-top-btn"
            onClick={scrollToTop}
            aria-label="Scroll to top"
          >
            <FaChevronUp />
          </button>
        )}
      </div>
    </>
  );
};

export default withAuth(ReadyProductsView, 'client');
